import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import { api } from '../services/api';
import type { FeedItem } from '../types/auth';
import { storage } from '../utils/storage';
import { useAuth } from './AuthContext';
import { feedNumericId, feedRefFor } from '../utils/feedRefId';

/**
 * Maps a FeedItemKind (or a UnifiedEvent.source value) to the API `source`
 * parameter used by api.saveEvent(source, id) / api.unsaveEvent(source, id).
 *
 * The BE endpoint is  POST/DELETE /api/events/{source}/{id}/save
 * where `source` is 'live' | 'room' | 'offline' | 'master' | 'practice'.
 * FeedItem doesn't carry `source` explicitly, so we derive it from `kind`.
 *
 * Legacy callers in CircleChatScreen / AgapeCenterScreen pass
 * UnifiedEvent.source ('master' | 'agape') here. Both map to the BE
 * 'master' source — Agape-curated events are stored alongside master-
 * curated ones in the same saved-events table on BE.
 */
export function feedKindToSource(kind: string): string {
  // UnifiedEvent.source pass-through (legacy callers).
  if (kind === 'master' || kind === 'agape') return 'master';
  if (kind.startsWith('live_'))     return 'live';
  if (kind.startsWith('room_'))     return 'room';
  if (kind.startsWith('offline_'))  return 'offline';
  // event_saved items already carry savedMasterEventId — use 'master'
  if (kind === 'event_saved')       return 'master';
  // practice_uploaded uses 'practice' (heart "in my practices" flow)
  if (kind === 'practice_uploaded') return 'practice';
  return 'live'; // safe fallback
}

export interface SavedEventsContextValue {
  /** Set of refIds the user has saved (derived from savedItems). */
  savedIds: Set<string>;
  /** Full snapshot list — used to render «Сохранения» / «Практики» feeds
   *  even when an item isn't in the auth user's getFeed() response. */
  savedItems: FeedItem[];
  /** Check whether a specific event (by refId) is saved. */
  isSaved: (refId: string) => boolean;
  /**
   * Toggle save status. The first form (refId, kind) is kept for legacy
   * callers; the second form passes a FeedItem snapshot so the saved card
   * can render later without the original feed being available.
   *
   * Always prefer the snapshot form when the caller has a FeedItem in scope.
   */
  toggleSave: (refIdOrItem: string | FeedItem, kind?: string) => Promise<void>;
  /** Apply bookmark result after a direct API call. Pass item to save, null to unsave. */
  applyBookmark: (item: FeedItem | null, refId: string) => void;
  /** True while the initial AsyncStorage load is in flight. */
  isLoading: boolean;
}

const SavedEventsContext = createContext<SavedEventsContextValue | null>(null);

/**
 * Until BE ships GET /api/user/me/saved we keep an authoritative local cache
 * of saved items in AsyncStorage. Persistence survives reloads (web +
 * native), so the «Сохранения» tab + every save-state icon survive a
 * refresh. Cross-device sync remains a BE follow-up — see
 * docs/backend/saved-events-list-endpoint.md.
 */
export const SavedEventsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [savedItemsMap, setSavedItemsMap] = useState<Map<string, FeedItem>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const loadedUserRef = useRef<string | null>(null);

  // Bootstrap saved snapshots from AsyncStorage on login. Falls back to
  // scraping getFeed() for legacy «event_saved» activity refs (only useful
  // when a master in your follow list saved the same item — historical
  // behaviour, kept so existing accounts don't regress).
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setSavedItemsMap(new Map());
      loadedUserRef.current = null;
      return;
    }
    if (loadedUserRef.current === user.id) return;
    loadedUserRef.current = user.id;

    (async () => {
      setIsLoading(true);
      try {
        const cached = await storage.getSavedEvents().catch(() => [] as FeedItem[]);
        const map = new Map<string, FeedItem>();
        for (const item of cached) {
          // CORE-1342 — здесь стояло `typeof item.refId === 'number'`, и оно
          // отсеивало ВСЁ, что пришло с сервера: там ссылка всегда строка.
          if (item?.refId != null) map.set(String(item.refId), item);
        }

        // Legacy back-fill: when the local cache is empty but the user's
        // followed-master feed contains «event_saved» activity cards
        // pointing at items the user previously saved on another device,
        // surface those refs so the bookmark icon stays correct in-feed.
        if (map.size === 0) {
          const feedRes = await api.getFeed({ limit: 200 }).catch(() => ({ items: [] as FeedItem[] }));
          for (const item of (feedRes as { items?: FeedItem[] }).items ?? []) {
            if (item.kind === 'event_saved') {
              map.set(item.refId, item);
              // Сохранённое событие мастера ссылается ещё и на исходное
              // событие — храним обе ссылки в одной форме, иначе закладка
              // не находится по той, с которой пришли.
              if (item.savedMasterEventId) {
                map.set(feedRefFor('master_event_created', item.savedMasterEventId), item);
              }
            }
          }
        }

        setSavedItemsMap(map);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn('[SavedEvents] Bootstrap failed:', msg);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isAuthenticated, user?.id]);

  const persist = useCallback(async (next: Map<string, FeedItem>) => {
    const items = Array.from(next.values());
    try {
      await storage.setSavedEvents(items);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[SavedEvents] persist failed:', msg);
    }
  }, []);

  const applyBookmark = useCallback((item: FeedItem | null, refId: string) => {
    setSavedItemsMap(prev => {
      const next = new Map(prev);
      if (item !== null) {
        next.set(refId, { ...item, isSaved: true });
      } else {
        next.delete(refId);
      }
      void persist(next);
      return next;
    });
  }, [persist]);

  const isSaved = useCallback(
    (refId: string) => savedItemsMap.has(refId),
    [savedItemsMap],
  );

  const toggleSave = useCallback(async (refIdOrItem: string | FeedItem, kindArg?: string) => {
    if (!isAuthenticated) return;

    const item: FeedItem | null = typeof refIdOrItem === 'object' && refIdOrItem !== null
      ? refIdOrItem
      : null;
    const refId = item ? item.refId : (refIdOrItem as string);
    const kind = item ? item.kind : (kindArg ?? 'live_scheduled');
    const source = feedKindToSource(kind);

    const wasSaved = savedItemsMap.has(refId);

    // Optimistic local update — keep snapshot when adding so consumers can
    // render a card immediately. Drop snapshot when removing.
    setSavedItemsMap(prev => {
      const next = new Map(prev);
      if (wasSaved) {
        next.delete(refId);
      } else if (item) {
        next.set(refId, { ...item, isSaved: true });
      } else {
        // Legacy form — no snapshot. Store a minimal stub so isSaved()
        // returns true; the «Сохранения» tab won't render this entry
        // until a snapshot lands (eg. via the in-feed item mark step).
        next.set(refId, {
          kind: kind as FeedItem['kind'],
          refId,
          actorUserId: '',
          actorName: '',
          actorAvatarUrl: null,
          title: '',
          coverImageUrl: null,
          startDateTimeUtc: null,
          durationMinutes: null,
          timestamp: new Date().toISOString(),
          savedMasterEventId: null,
          savedEventTitle: null,
          isSaved: true,
        });
      }
      // Fire-and-forget persistence; no need to block the UI on it.
      void persist(next);
      return next;
    });

    try {
      // На сервер уходит числовой id сущности, а не ссылка целиком.
      // Круги (Guid) сюда не попадают — их карточки не сохраняются.
      const entityId = feedNumericId(refId);
      if (entityId == null) return;
      if (wasSaved) {
        await api.unsaveEvent(source, entityId);
      } else {
        await api.saveEvent(source, entityId);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[SavedEvents] toggleSave failed, keeping optimistic state:', msg);
    }
  }, [isAuthenticated, savedItemsMap, persist]);

  // Only renderable snapshots — legacy callers (no FeedItem in scope) store
  // a stub with an empty title so isSaved() works, but those stubs would
  // render as blank cards in «Сохранения». Filter them out here so the
  // bookmark badge stays correct on in-feed surfaces while the saved tab
  // only shows full snapshots.
  const savedItems = useMemo(
    () => Array.from(savedItemsMap.values()).filter(it => typeof it.title === 'string' && it.title.length > 0),
    [savedItemsMap],
  );
  const savedIds = useMemo(() => new Set(savedItemsMap.keys()), [savedItemsMap]);

  const value = useMemo<SavedEventsContextValue>(
    () => ({ savedIds, savedItems, isSaved, toggleSave, applyBookmark, isLoading }),
    [savedIds, savedItems, isSaved, toggleSave, applyBookmark, isLoading],
  );

  return (
    <SavedEventsContext.Provider value={value}>
      {children}
    </SavedEventsContext.Provider>
  );
};

export const useSavedEvents = (): SavedEventsContextValue => {
  const context = useContext(SavedEventsContext);
  if (!context) {
    throw new Error('useSavedEvents must be used within a SavedEventsProvider');
  }
  return context;
};
